import { Component, OnInit, OnDestroy, Input, SecurityContext } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject, takeUntil } from 'rxjs';
import { DomSanitizer } from '@angular/platform-browser';
import { LatestExportsService } from './latest-exports.service';
import { NotificationItem } from '../../notification-item';
import { WindowRef } from '../core/domain/window-ref';

@Component({
  selector: 'adminui-latest-export',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './latest-export.component.html',
})
export class LatestExportComponent implements OnInit, OnDestroy {
  @Input({ alias: 'message', required: true }) message: { [key: string]: string } = {};

  exports: NotificationItem[] = [];
  private destroy$ = new Subject<void>();

  constructor(
    private latestExportsService: LatestExportsService,
    private windowRef: WindowRef, // Fixed typo
    private sanitizer: DomSanitizer // Added for security
  ) { }

  ngOnInit(): void {
    // Single source of truth: subscribe to the stream
    this.latestExportsService.latestExports$
      .pipe(takeUntil(this.destroy$))
      .subscribe((exports) => {
        this.exports = exports;
      });
  }

  downloadExport(event: Event, exportItem: NotificationItem): void {
    event.preventDefault();
    if (!exportItem.url) return;

    // SECURITY: Sanitize the URL to prevent XSS (javascript: links)
    const sanitizedUrl = this.sanitizer.sanitize(SecurityContext.URL, exportItem.url);

    if (!sanitizedUrl) {
      console.warn('Blocked a potentially malicious export URL:', exportItem.url);
      return;
    }

    // SECURITY: Validate URL to prevent open redirect attacks
    if (!this.isUrlTrusted(sanitizedUrl)) {
      console.error('Blocked untrusted export URL:', sanitizedUrl);
      // Optionally, show user-friendly error message
      return;
    }

    exportItem.isViewed = true;
    this.latestExportsService.markAsViewed(exportItem.notificationId);
    // Open sanitized and validated URL in a new tab (now with noopener/noreferrer)
    this.windowRef.open(sanitizedUrl, '_blank');
  }


  /**
  * Validates that a URL is from a trusted source to prevent open redirect attacks.
  * @param url The URL to validate
  * @returns true if the URL is trusted, false otherwise
  */
  private isUrlTrusted(url: string): boolean {
    try {
      // IMPORTANT: Only accept absolute URLs (must start with http:// or https://)
      // Relative URLs are security risks for external exports
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        return false;
      }

      // Parse the absolute URL
      const urlObj = new URL(url);

      // Only allow http and https protocols (block javascript:, data:, file:, etc.)
      if (urlObj.protocol !== 'http:' && urlObj.protocol !== 'https:') {
        return false;
      }

      // Whitelist approach: Only allow URLs from trusted domains
      // Add your trusted domains here
      const trustedDomains = [
        this.windowRef.getHostname(),
        'stag-odp-export-queue.s3.eu-west-1.amazonaws.com',
        'prod-odp-export-queue.s3.eu-west-1.amazonaws.com'
      ];

      // Check if hostname matches any trusted domain (including subdomains)
      const isTrusted = trustedDomains.some(domain => {
        // Exact match
        if (urlObj.hostname === domain) {
          return true;
        }
        // Subdomain match: ensure it ends with .domain to prevent domain squatting
        // e.g., 'sub.example.com' matches 'example.com', but 'evilexample.com' does not
        if (urlObj.hostname.endsWith('.' + domain)) {
          return true;
        }
        return false;
      });

      return isTrusted;
    } catch (e) {
      // Invalid URL - return false, logging is done in downloadExport
      return false;
    }
  }

  clearExport(event: Event, exportItem: NotificationItem): void {
    event.preventDefault();
    event.stopPropagation();
    this.latestExportsService.removeExport(exportItem.notificationId);
  }

  clearAllExports(event: Event): void {
    event.preventDefault();
    this.latestExportsService.clearAll();
  }

  minimizePanel(event: Event): void {
    event.preventDefault();
    this.latestExportsService.closeLatestExportsPanel();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}